/** Tax and finance settings (§4.2.5). Changes apply to new records only. */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { percent, shortDate } from '../../lib/format';
import {
  Alert,
  Button,
  Card,
  Field,
  Skeleton,
  TableWrap,
  Td,
  Th,
  inputClass,
} from '../../components/ui';

interface TaxSetting {
  id: string;
  effectiveFrom: string;
  vatRate: string;
  ewtGoods: string;
  ewtServices: string;
  wvatGov: string;
  ewtCommission: string;
  lowMarginThreshold: string;
  agingBasis: 'BILLING_DATE' | 'DUE_DATE';
}

const asPercent = (value: string | number): string => String(Number(value) * 100);

export function TaxSettingsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    vatRate: '12',
    ewtGoods: '1',
    ewtServices: '2',
    wvatGov: '5',
    ewtCommission: '10',
    lowMarginThreshold: '10',
    agingBasis: 'BILLING_DATE' as TaxSetting['agingBasis'],
    effectiveFrom: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const settings = useQuery({
    queryKey: ['tax-settings'],
    queryFn: () => api<{ data: TaxSetting | null; history: TaxSetting[] }>('/settings/tax'),
  });

  useEffect(() => {
    const current = settings.data?.data;
    if (!current) return;
    setForm({
      vatRate: asPercent(current.vatRate),
      ewtGoods: asPercent(current.ewtGoods),
      ewtServices: asPercent(current.ewtServices),
      wvatGov: asPercent(current.wvatGov),
      ewtCommission: asPercent(current.ewtCommission),
      lowMarginThreshold: asPercent(current.lowMarginThreshold),
      agingBasis: current.agingBasis,
      effectiveFrom: '',
    });
  }, [settings.data]);

  const save = useMutation({
    mutationFn: () =>
      api('/settings/tax', {
        method: 'POST',
        body: {
          vatRate: Number(form.vatRate) / 100,
          ewtGoods: Number(form.ewtGoods) / 100,
          ewtServices: Number(form.ewtServices) / 100,
          wvatGov: Number(form.wvatGov) / 100,
          ewtCommission: Number(form.ewtCommission) / 100,
          lowMarginThreshold: Number(form.lowMarginThreshold) / 100,
          agingBasis: form.agingBasis,
          ...(form.effectiveFrom ? { effectiveFrom: form.effectiveFrom } : {}),
        },
      }),
    onSuccess: () => {
      setError(null);
      setNotice('Saved. Existing leads and billings keep the rates they were created with.');
      void queryClient.invalidateQueries({ queryKey: ['tax-settings'] });
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not save the settings.'),
  });

  if (settings.isLoading) return <Skeleton className="h-96 w-full" />;

  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <header>
        <h1 className="text-xl font-bold text-navy dark:text-white">Tax &amp; finance settings</h1>
        <p className="text-sm text-slate-500">
          Rates for new records. Saving creates a new effective-dated set; nothing already entered
          changes.
        </p>
      </header>

      {notice && <Alert tone="ok">{notice}</Alert>}
      {error && <Alert tone="critical">{error}</Alert>}

      <Card title="Current rates">
        <form
          className="grid gap-4 sm:grid-cols-2"
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate();
          }}
        >
          <Field label="VAT (%)" hint="12% under the National Internal Revenue Code." required>
            <input
              className={inputClass}
              type="number"
              step="0.01"
              required
              value={form.vatRate}
              onChange={(event) => setForm({ ...form, vatRate: event.target.value })}
            />
          </Field>
          <Field label="Expanded withholding tax — goods (%)" required>
            <input
              className={inputClass}
              type="number"
              step="0.01"
              required
              value={form.ewtGoods}
              onChange={(event) => setForm({ ...form, ewtGoods: event.target.value })}
            />
          </Field>
          <Field label="Expanded withholding tax — services (%)" required>
            <input
              className={inputClass}
              type="number"
              step="0.01"
              required
              value={form.ewtServices}
              onChange={(event) => setForm({ ...form, ewtServices: event.target.value })}
            />
          </Field>
          <Field label="Withholding VAT — government (%)" required>
            <input
              className={inputClass}
              type="number"
              step="0.01"
              required
              value={form.wvatGov}
              onChange={(event) => setForm({ ...form, wvatGov: event.target.value })}
            />
          </Field>
          <Field label="Withholding on commissions (%)" required>
            <input
              className={inputClass}
              type="number"
              step="0.01"
              required
              value={form.ewtCommission}
              onChange={(event) => setForm({ ...form, ewtCommission: event.target.value })}
            />
          </Field>
          <Field
            label="Low margin warning below (%)"
            hint="A red badge appears on leads under this net margin."
            required
          >
            <input
              className={inputClass}
              type="number"
              step="0.01"
              required
              value={form.lowMarginThreshold}
              onChange={(event) => setForm({ ...form, lowMarginThreshold: event.target.value })}
            />
          </Field>
          <Field label="Receivables ageing counts from" required>
            <select
              className={inputClass}
              value={form.agingBasis}
              onChange={(event) =>
                setForm({ ...form, agingBasis: event.target.value as TaxSetting['agingBasis'] })
              }
            >
              <option value="BILLING_DATE">Billing date</option>
              <option value="DUE_DATE">Due date</option>
            </select>
          </Field>
          <Field label="Effective from" hint="Leave empty to apply from now.">
            <input
              className={inputClass}
              type="date"
              value={form.effectiveFrom}
              onChange={(event) => setForm({ ...form, effectiveFrom: event.target.value })}
            />
          </Field>
          <div className="sm:col-span-2">
            <Button type="submit" busy={save.isPending}>
              <Save aria-hidden="true" className="h-4 w-4" /> Save new rates
            </Button>
          </div>
        </form>
      </Card>

      <Card title="History" description="Every change is kept, so old records can be explained.">
        <TableWrap>
          <thead>
            <tr>
              <Th>Effective from</Th>
              <Th align="right">VAT</Th>
              <Th align="right">EWT goods</Th>
              <Th align="right">EWT services</Th>
              <Th align="right">WVAT gov</Th>
              <Th align="right">Commission</Th>
              <Th>Ageing</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {settings.data?.history.map((row) => (
              <tr key={row.id}>
                <Td>{shortDate(row.effectiveFrom)}</Td>
                <Td align="right">{percent(row.vatRate)}</Td>
                <Td align="right">{percent(row.ewtGoods)}</Td>
                <Td align="right">{percent(row.ewtServices)}</Td>
                <Td align="right">{percent(row.wvatGov)}</Td>
                <Td align="right">{percent(row.ewtCommission)}</Td>
                <Td>{row.agingBasis === 'BILLING_DATE' ? 'Billing date' : 'Due date'}</Td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      </Card>
    </div>
  );
}
